import { AxiosError } from 'axios'
import _isEqual from 'lodash/isEqual'
import _throttle from 'lodash/throttle'
import { ref, Ref, watch } from '@vue/composition-api'
import { getDepartments, getDepartment, putDepartment, deleteDepartment } from '@/api'
import store from '@/store'
import { Pagination, emptyPagination } from '@/components/composables/usePagination'
import { DepartmentID, Department } from '@shared/sm/departments/types'
import { useLoading } from '@coursedog/composables'

export default function useDepartments ({
  school = store.getters.school,
  pagination = ref(emptyPagination()),
}: {
  school?: string,
  pagination?: Ref<Pagination>,
} = {
  school: store.getters.school,
  pagination: ref(emptyPagination()),
}) {
  const { startLoading, stopLoading, getIsLoading } = useLoading()

  const departments: Ref<Department[]> = ref([])
  const totalCount: Ref<number> = ref(0)

  async function fetchDepartments ({
    paginationRef = pagination,
  }: {
    paginationRef: Ref<Pagination>,
  } = {
    paginationRef: pagination,
  }) {
    const shouldPaginate = Boolean(paginationRef.value.limit || paginationRef.value.skip)

    // getDepartments returns two different interfaces
    // based on whether skip and/or limit are provided.
    if (shouldPaginate) {
      const config = {
        school,
        skip: paginationRef.value.skip,
        limit: paginationRef.value.limit,
      }
      const results = await getDepartments(config) as unknown as {
        data: Department[],
        totalCount: number,
      }

      departments.value = results.data
      totalCount.value = results.totalCount
    } else {
      const config = {
        school,
      }
      const results = await getDepartments(config) as unknown as Record<DepartmentID, Department>

      departments.value = Object.values(results)
      totalCount.value = Object.values(results).length
    }
  }

  async function fetchDepartmentById (departmentId: DepartmentID) {
    startLoading('fetchDepartmentById')

    const response = await getDepartment({
      school,
      department: departmentId,
    }) as unknown as Department & { error: AxiosError }

    stopLoading('fetchDepartmentById')
    if (response.error instanceof Error) {
      throw response.error
    }
    return response
  }

  async function updateDepartment (department: Department) {
    startLoading('updateDepartment')

    const response = await putDepartment(
      { school, department: department.id },
      department,
    ) as unknown as Department & { error: AxiosError }

    stopLoading('updateDepartment')
    if (response.error instanceof Error) {
      throw response.error
    }
    return response
  }

  async function removeDepartment (departmentId: DepartmentID) {
    startLoading('removeDepartment')
    await deleteDepartment({ school, department: departmentId })
    stopLoading('removeDepartment')
  }

  watch(pagination, (newPagination, oldPagination) => {
    // Pagination watcher triggers with any change in Url,
    // even when pagination didn't really change (e.g selecting Columns).
    const paginationChanged = !_isEqual(newPagination, oldPagination)
    if (paginationChanged) {
      fetchDepartmentsThrottled()
    }
  })

  const fetchDepartmentsThrottled = _throttle(fetchDepartments, 500)

  return {
    departments,
    totalCount,
    fetchDepartments,
    fetchDepartmentById,
    isFetchingDepartmentById: getIsLoading('fetchDepartmentById'),
    updateDepartment,
    isUpdatingDepartment: getIsLoading('updateDepartment'),
    removeDepartment,
    isDeletingDepartment: getIsLoading('removeDepartment'),
  }
}
